import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import HotTubOutlinedIcon from "@mui/icons-material/HotTubOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import KingBedOutlinedIcon from "@mui/icons-material/KingBedOutlined";
import LocalDiningOutlinedIcon from "@mui/icons-material/LocalDiningOutlined";
import LocalLaundryServiceOutlinedIcon from "@mui/icons-material/LocalLaundryServiceOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import { ContentBand, Inner, PageIntro, SectionHeader, SpecialTourLayout } from "./SpecialTourLayout";

const asset = (fileName: string) => `/special-tour-assets/${fileName}`;

const hotelHighlights = [
  {
    title: "오비히로 시내 거점",
    body: "골프장 이동과 저녁 자유시간을 함께 고려하기 좋은 오비히로 중심권 숙소입니다.",
  },
  {
    title: "객실 리모델링 완료",
    body: "저희가 트윈/더블 리모델링 객실을 중심으로 안내하며, 실제 객실 배정은 예약 시 함께 확인합니다.",
  },
  {
    title: "번화가 도보권",
    body: "호텔에서 남쪽으로 약 500m~900m 거리에 식당가와 포장마차 거리가 이어집니다.",
  },
];

const rateRows = [
  { season: "극성수기", months: "7월, 8월", normal: "주중 12,000엔 / 주말 18,000엔", member: "주중 8,000엔 / 주말 10,000엔" },
  { season: "성수기", months: "5월, 6월, 9월, 10월", normal: "주중 9,000엔 / 주말 15,000엔", member: "주중 6,000엔 / 주말 8,000엔" },
  { season: "비수기", months: "11월~4월", normal: "주중 4,000엔 / 주말 7,000엔", member: "주중 3,500엔 / 주말 6,000엔" },
];

const longStayCoupons = [
  { nights: "50박", total: "300,000엔", unit: "1박 6,000엔" },
  { nights: "100박", total: "550,000엔", unit: "1박 5,500엔" },
];

function PriceLines({ value, accent = false }: { value: string; accent?: boolean }) {
  return (
    <span className={`grid gap-1 [word-break:keep-all] ${accent ? "font-800 text-[#1d5d31]" : ""}`}>
      {value.split(" / ").map((item) => (
        <span key={item} className="whitespace-nowrap">
          {item}
        </span>
      ))}
    </span>
  );
}

const amenityItems = [
  {
    icon: <WifiOutlinedIcon />,
    title: "무료 Wi-Fi",
    body: "Net-명 : OBIHIRO(4-8R) 이며, 별도의 비밀번호 없이 접속이 가능합니다.",
  },
  {
    icon: <LocalDiningOutlinedIcon />,
    title: "조식 예약제",
    body: "1층 조식 공간에서 06:00~09:00 운영, 1인 1,000엔 기준입니다. 체크인 당일 신청 가능 여부를 확인합니다.",
  },
  {
    icon: <LocalLaundryServiceOutlinedIcon />,
    title: "코인 세탁",
    body: "7층에 24시간 코인 세탁 공간이 있어 장기 일정이나 연박 고객에게 편리합니다.",
  },
  {
    icon: <HotTubOutlinedIcon />,
    title: "인근 온천 연계",
    body: "호텔 내 대중탕은 없고 인근 온천을 이용합니다. 셔틀 시간과 비용은 저희가 예약 시 다시 확인해 안내합니다.",
  },
  {
    icon: <WaterDropOutlinedIcon />,
    title: "음용수 안내",
    body: "수도물을 음료용으로 사용하기 때문에 별도의 생수병을 비치하고 있지 않습니다.",
  },
  {
    icon: <KeyOutlinedIcon />,
    title: "객실 열쇠",
    body: "외출 시 객실키는 개인이 소지하고 체크아웃 시 반납합니다. 분실 시 별도 요금이 발생할 수 있습니다.",
  },
];

export function HotelsPage() {
  return (
    <SpecialTourLayout>
      <PageIntro
        eyebrow="Stay"
        title="오비히로 그랜드 호텔"
        body="홋카이도 오비히로 골프 여행에서 숙소와 저녁 동선을 함께 잡기 좋은 거점 호텔입니다. 저희가 객실, 요금, 편의시설, 주변 식당가 정보를 확인해 정리했습니다."
        afterBody={
          <div className="rounded-lg border border-[#d9ded5] bg-white p-5 shadow-sm">
            <h2 className="text-xl font-900 leading-tight text-[#15391f] [word-break:keep-all] md:text-2xl">
              ◆ 숙소정보 ◆
            </h2>
            <dl className="mt-4 grid gap-3 text-sm leading-7 text-[#344138] md:text-base">
              <div className="grid gap-1 sm:grid-cols-[96px_1fr]">
                <dt className="font-900 text-[#15391f]">Address</dt>
                <dd className="[word-break:keep-all]">
                  20, minami 3 chome, nishi 2 Jo, Obihiro-si, Hokkaido, Japan
                  <span className="block">(우) 080-0012</span>
                </dd>
              </div>
              <div className="grid gap-1 sm:grid-cols-[96px_1fr]">
                <dt className="font-900 text-[#15391f]">Homepage</dt>
                <dd>
                  <a href="http://www.ogh.co.jp/" className="font-800 text-[#1d5d31] hover:text-[#15391f]">
                    http://www.ogh.co.jp/
                  </a>
                </dd>
              </div>
              <div className="grid gap-1 sm:grid-cols-[96px_1fr]">
                <dt className="font-900 text-[#15391f]">Tel</dt>
                <dd>+81) 155 - 28 - 3007</dd>
              </div>
            </dl>
          </div>
        }
        image={asset("obihiro-grand-hotel-counter.png")}
      />

      <ContentBand className="bg-white">
        <Inner>
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <SectionHeader
                eyebrow="Hotel Overview"
                title={
                  <>
                    <span className="block">골프 일정 후 쉬기 좋은</span>
                    <span className="block">오비히로 시내 숙소</span>
                  </>
                }
                body="라운드, 차량 이동, 저녁 식사 동선을 한 번에 보기 좋도록 저희가 핵심 정보를 홈페이지용으로 다시 정리했습니다."
              />
              <div className="grid gap-4 sm:grid-cols-3">
                {hotelHighlights.map((item) => (
                  <div key={item.title} className="rounded-lg border border-[#d9ded5] bg-[#fbfaf6] p-5 shadow-sm">
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#e8f2e6] text-[#1e6b34]">
                      <HotelOutlinedIcon />
                    </div>
                    <h2 className="text-lg font-900 text-[#15391f]">{item.title}</h2>
                    <p className="mt-3 text-sm leading-7 text-[#596559]">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-[#d9ded5] bg-[#fbfaf6] shadow-sm">
              <img src={asset("obihiro-grand-hotel-guide.png")} alt="오비히로 그랜드 호텔 안내 이미지" className="w-full object-cover" />
            </div>
          </div>
        </Inner>
      </ContentBand>

      <ContentBand className="bg-[#f7f5ef]">
        <Inner>
          <div className="grid min-w-0 gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div className="min-w-0 overflow-hidden rounded-lg border border-[#d9ded5] bg-white shadow-sm">
              <img src={asset("obihiro-grand-hotel-room-rates.png")} alt="오비히로 그랜드 호텔 객실 안내와 요금표" className="w-full object-cover" />
            </div>
            <div className="min-w-0">
              <SectionHeader
                eyebrow="Rooms & Rates"
                title="리모델링 객실과 2026년 요금 기준"
                body="저희가 확인한 1인 기준 요금입니다. 조식은 별도 선택이며, 실제 적용 요금과 객실 가능 여부는 상담 시 다시 확인해 드립니다."
              />
              <div className="min-w-0 overflow-hidden rounded-lg border border-[#d9ded5] bg-white shadow-sm">
                <div className="grid gap-3 p-4 md:hidden">
                  {rateRows.map((row) => (
                    <div key={row.season} className="rounded-lg border border-[#edf0ea] bg-[#fbfaf6] p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-lg font-900 text-[#15391f]">{row.season}</h3>
                        <span className="rounded-full bg-[#e8f2e6] px-3 py-1 text-xs font-900 text-[#1d5d31]">
                          {row.months}
                        </span>
                      </div>
                      <dl className="mt-4 grid gap-3 text-sm leading-6">
                        <div>
                          <dt className="font-900 text-[#596559]">일반가</dt>
                          <dd className="mt-1 text-[#3d4a41]">
                            <PriceLines value={row.normal} />
                          </dd>
                        </div>
                        <div>
                          <dt className="font-900 text-[#596559]">OB72 회원가</dt>
                          <dd className="mt-1">
                            <PriceLines value={row.member} accent />
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
                <div className="hidden max-w-full overflow-x-auto md:block">
                  <table className="w-full table-fixed text-left text-sm">
                    <colgroup>
                      <col className="w-[15%]" />
                      <col className="w-[24%]" />
                      <col className="w-[29%]" />
                      <col className="w-[32%]" />
                    </colgroup>
                    <thead className="bg-[#15391f] text-white">
                      <tr>
                        <th className="px-4 py-3 font-900 [word-break:keep-all]">구분</th>
                        <th className="px-4 py-3 font-900 [word-break:keep-all]">기간</th>
                        <th className="px-4 py-3 font-900 [word-break:keep-all]">일반가</th>
                        <th className="whitespace-nowrap px-4 py-3 font-900">OB72 회원가</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#edf0ea]">
                      {rateRows.map((row) => (
                        <tr key={row.season}>
                          <td className="px-4 py-4 font-900 text-[#15391f]">{row.season}</td>
                          <td className="px-4 py-4 text-[#596559]">{row.months}</td>
                          <td className="px-4 py-4 text-[#3d4a41]">
                            <PriceLines value={row.normal} />
                          </td>
                          <td className="px-4 py-4">
                            <PriceLines value={row.member} accent />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="grid gap-3 border-t border-[#edf0ea] bg-[#fbfaf6] p-5 text-sm leading-7 text-[#596559] md:grid-cols-2">
                  <div className="flex gap-3">
                    <KingBedOutlinedIcon className="text-[#1e6b34]" />
                    <span>객실 타입은 트윈/더블 리모델링 객실을 기준으로 저희가 안내합니다.</span>
                  </div>
                  <div className="flex gap-3">
                    <LocalDiningOutlinedIcon className="text-[#1e6b34]" />
                    <span>조식은 별도 선택 가능하며 저희가 확인한 금액은 1,000엔입니다.</span>
                  </div>
                </div>
                <div className="border-t border-[#edf0ea] bg-[#fffdf2] p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-lg font-900 text-[#15391f]">장기투숙 할인쿠폰</h3>
                      <p className="mt-2 text-sm leading-7 text-[#596559]">
                        저희가 확인한 50박권과 100박권은 주중/주말 모두 사용 가능합니다.
                      </p>
                    </div>
                    <div className="grid gap-2 text-sm md:min-w-[300px]">
                      {longStayCoupons.map((coupon) => (
                        <div
                          key={coupon.nights}
                          className="grid grid-cols-[52px_100px_1fr] gap-3 rounded-lg bg-white px-4 py-3 shadow-sm"
                        >
                          <span className="font-900 text-[#d16339]">{coupon.nights}</span>
                          <span className="whitespace-nowrap text-[#3d4a41]">{coupon.total}</span>
                          <span className="whitespace-nowrap text-right font-800 text-[#1d5d31]">{coupon.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="mt-3 text-xs font-700 leading-6 text-[#d16339]">
                    단, 할인쿠폰은 당해 연도에만 사용 가능합니다.
                  </p>
                  <div className="mt-4 rounded-lg border border-[#f3c9bc] bg-white/85 p-4">
                    <h4 className="text-lg font-900 leading-tight text-[#d12f1f] [word-break:keep-all]">
                      ☞ 할인권 환불조건
                    </h4>
                    <p className="mt-3 text-sm font-800 leading-7 text-[#15391f] [word-break:keep-all]">
                      50일, 100일 숙박할인권 구입 후 당해 잔여숙박일에 대한 환불조건은 10/31 기준 잔여일 중 최대 10박까지 환불해 드립니다.
                    </p>
                    <div className="mt-3 text-sm leading-7 text-[#596559] [word-break:keep-all]">
                      <p className="font-800 text-[#15391f]">(사용예)</p>
                      <ul className="mt-1 grid gap-1">
                        <li>- 50박 할인권 구매 후 42박 사용 후 잔여일 8박 남았을 경우 8박 환불 가능합니다.</li>
                        <li>- 50박 할인권 구매 후 38박 사용 후 잔여일 12박 남았을 경우 10박까지만 환불 가능합니다.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Inner>
      </ContentBand>

      <ContentBand className="bg-white">
        <Inner>
          <SectionHeader
            eyebrow="Amenities"
            title="투숙 전 알아두면 좋은 이용 안내"
            body="저희가 확인한 호텔 이용 정보를 여행자 입장에서 바로 이해할 수 있도록 정리했습니다."
          />
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {amenityItems.map((item) => (
              <article key={item.title} className="rounded-lg border border-[#d9ded5] bg-[#fbfaf6] p-6 shadow-sm">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#e8f2e6] text-[#1e6b34]">
                  {item.icon}
                </div>
                <h2 className="text-xl font-900 text-[#15391f]">{item.title}</h2>
                <p className="mt-3 text-sm leading-7 text-[#596559]">{item.body}</p>
              </article>
            ))}
          </div>
          <div className="mt-6 grid gap-4 rounded-lg border border-[#d9ded5] bg-[#fbfaf6] p-5 text-sm leading-7 text-[#596559] md:grid-cols-[auto_1fr] md:items-center">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-white text-[#1e6b34]">
              <CleaningServicesOutlinedIcon />
            </div>
            <p>
              연박 고객은 투숙 중 객실 청소가 기본 제공되지 않고 수건류는 매일 제공됩니다. 청소 요청 시 저희가 확인한 금액은 1회
              1,500엔입니다.
            </p>
          </div>
        </Inner>
      </ContentBand>

      <ContentBand className="bg-[#f7f5ef]">
        <Inner>
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <SectionHeader
                eyebrow="Nearby"
                title="저녁 자유시간은 호텔 주변 식당가로"
                body="저희가 확인한 동선으로 호텔에서 남쪽으로 약 500m~900m 이동하면 번화가와 식당가가 이어집니다. 골프 후 가볍게 식사와 산책을 붙이기 좋습니다."
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-[#d9ded5] bg-white p-5 shadow-sm">
                  <MapOutlinedIcon className="text-[#1e6b34]" />
                  <h2 className="mt-3 text-lg font-900 text-[#15391f]">번화가 500m~900m</h2>
                  <p className="mt-3 text-sm leading-7 text-[#596559]">저희가 확인한 주변 지도를 바탕으로 저녁 동선을 제안합니다.</p>
                </div>
                <div className="rounded-lg border border-[#d9ded5] bg-white p-5 shadow-sm">
                  <ConfirmationNumberOutlinedIcon className="text-[#1e6b34]" />
                  <h2 className="mt-3 text-lg font-900 text-[#15391f]">장기투숙 쿠폰</h2>
                  <p className="mt-3 text-sm leading-7 text-[#596559]">50일권, 100일권 할인쿠폰과 온천이용권 이벤트도 저희가 함께 확인해 안내합니다.</p>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <img
                src={asset("obihiro-grand-hotel-restaurant-street.png")}
                alt="오비히로 그랜드 호텔 주변 식당거리"
                className="h-80 w-full rounded-lg object-cover shadow-sm"
              />
              <img
                src={asset("obihiro-grand-hotel-yatai-street.png")}
                alt="오비히로 그랜드 호텔 주변 포장마차 거리"
                className="h-80 w-full rounded-lg object-cover shadow-sm"
              />
            </div>
          </div>
        </Inner>
      </ContentBand>

    </SpecialTourLayout>
  );
}
